/**
 * The refusals of `PUT products/{id}/variants/{variantId}/barcodes`, from
 * bos-api `ProductErrorCodes` (API-195). Each is a 400 that changes nothing;
 * a failed call answers with the `ErrorResponse`, whose `code` is one of
 * these. `BARCODE_TOO_LONG`, `BARCODE_DUPLICATE` and `BARCODE_TAKEN` name the
 * offending code in the message. An unknown product or variant is the 404
 * `PRODUCT_NOT_FOUND`, as for the product's other routes.
 */
export const PRODUCT_BARCODE_ERROR_CODES = [
    /** The main barcode is blank, or an alternate is. */
    'BARCODE_REQUIRED',
    /** A code is longer than 50 characters after trimming. */
    'BARCODE_TOO_LONG',
    /** A code is repeated, or an alternate equals the main, case-insensitively. */
    'BARCODE_DUPLICATE',
    /** The main barcode's regenerated unique key is already another variant's. */
    'BARCODE_TAKEN',
] as const;

export type ProductBarcodeErrorCode =
    (typeof PRODUCT_BARCODE_ERROR_CODES)[number];

export const isProductBarcodeErrorCode = (
    raw: unknown,
): raw is ProductBarcodeErrorCode =>
    typeof raw === 'string' &&
    (PRODUCT_BARCODE_ERROR_CODES as readonly string[]).includes(raw);

/**
 * The refusals of `POST products/barcode-issues/fix` (bos-api API-207), from
 * `ProductErrorCodes`. Nothing is applied on any of them; the body is
 * `{ error, code, itemIndex? }` and the service hands `itemIndex` on as
 * `BarcodeFixError.itemIndex`.
 */
export const BARCODE_FIX_ERROR_CODES = [
    /** 400: no items, more than 2,000, a missing field, an unknown role or action. No `itemIndex`. */
    'BARCODE_FIX_INVALID',
    /** 400: `generate` on an alternate, or `remove` on a main. */
    'BARCODE_ACTION_INVALID',
    /** 400: a typed code that is not 1–14 ASCII digits. */
    'BARCODE_NOT_DIGITS',
    /**
     * 400: the same row sent twice (the second), or a typed code the variant
     * would hold twice (the last row that typed it; no `itemIndex` when the
     * variant already held it twice).
     */
    'BARCODE_DUPLICATE',
    /**
     * 400: the typed main's unique key is another variant's, as in the
     * barcodes drawer; no `itemIndex` when only the database caught it.
     */
    'BARCODE_TAKEN',
    /** 409: a row's barcode changed since the list was read (the first such row). */
    'BARCODE_CHANGED',
] as const;

export type BarcodeFixErrorCode = (typeof BARCODE_FIX_ERROR_CODES)[number];

export const isBarcodeFixErrorCode = (
    raw: unknown,
): raw is BarcodeFixErrorCode =>
    typeof raw === 'string' &&
    (BARCODE_FIX_ERROR_CODES as readonly string[]).includes(raw);
